// Maps aliased tracking params (e.g. Triple Whale tw_*) from landing-page URLs onto empty utm_* columns.
import type { AttributionOrder } from "./channel-config"
import type { StoredJourneyMoment } from "./attribution-models"

export type UtmTargetField = "utm_source" | "utm_medium" | "utm_campaign" | "utm_term" | "utm_content"

export interface UrlParamAliasRule {
  param: string
  mapsTo: UtmTargetField
  // Used when the param is absent but another rule of the same set matched.
  fallback?: string
}

export interface UrlParamAliasSet {
  name: string
  rules: UrlParamAliasRule[]
  /** @deprecated superseded by per-rule `fallback`; still honoured for stored configs. */
  implied?: Partial<Record<UtmTargetField, string>>
}

// tw_* templates carry no medium param but are paid-only → the fallback keeps google+cpc matchers working.
export const DEFAULT_URL_PARAM_ALIAS_SETS: UrlParamAliasSet[] = [
  {
    name: "Triple Whale",
    rules: [
      { param: "tw_source", mapsTo: "utm_source" },
      { param: "tw_medium", mapsTo: "utm_medium", fallback: "cpc" },
      { param: "tw_campaign", mapsTo: "utm_campaign" },
      { param: "tw_adid", mapsTo: "utm_content" },
      { param: "tw_kwdid", mapsTo: "utm_term" },
    ],
  },
]

// Stored custom sets fully replace platform defaults; NULL/undefined means defaults apply.
export function resolveAliasSets(stored: UrlParamAliasSet[] | null | undefined): UrlParamAliasSet[] {
  return stored ?? DEFAULT_URL_PARAM_ALIAS_SETS
}

const VISIT_SIDES = ["first", "last"] as const

type VisitSide = (typeof VISIT_SIDES)[number]

type UtmOrderKey = `${VisitSide}_${UtmTargetField}`

// Fill-only-empty: Shopify-parsed utm wins; each visit side resolves from its own sources.
export function applyUrlParamAliases(order: AttributionOrder, sets: UrlParamAliasSet[]): AttributionOrder {
  if (sets.length === 0) {
    return order
  }

  const fills: Partial<Record<UtmOrderKey, string>> = {}

  for (const side of VISIT_SIDES) {
    const sources = collectParamSources(order, side)
    if (sources.length === 0) continue

    const getParam = (name: string): string | undefined => {
      for (const params of sources) {
        const value = params.get(name)?.trim()
        if (value) return value
      }
      return undefined
    }

    // Real params of ALL sets first, so a fallback never outranks a value another set measured.
    const matchedSets = sets.filter((set) => {
      let anyRuleMatched = false

      for (const rule of set.rules) {
        const value = getParam(rule.param)
        if (!value) continue

        anyRuleMatched = true
        fillIfEmpty(order, fills, side, rule.mapsTo, value)
      }

      return anyRuleMatched
    })

    for (const set of matchedSets) {
      for (const rule of set.rules) {
        const fallback = rule.fallback?.trim()
        if (fallback) {
          fillIfEmpty(order, fills, side, rule.mapsTo, fallback)
        }
      }
    }

    for (const set of matchedSets) {
      if (!set.implied) continue

      for (const [field, value] of Object.entries(set.implied) as [UtmTargetField, string][]) {
        const implied = value?.trim()
        if (implied) {
          fillIfEmpty(order, fills, side, field, implied)
        }
      }
    }
  }

  if (Object.keys(fills).length === 0) {
    return order
  }

  const aliased: AttributionOrder = { ...order, ...fills }
  const timeline = fillFirstMoment(order, fills)
  if (timeline) {
    aliased.journey_moments = timeline
  }
  return aliased
}

// A complete timeline (every moment Shopify counted is stored) starts at the first visit, so that
// session receives the first visit's fills too and Last Non-Direct Click sees the same aliases as
// Last Click. A truncated timeline starts at a later session and is left alone.
function fillFirstMoment(
  order: AttributionOrder,
  fills: Partial<Record<UtmOrderKey, string>>,
): StoredJourneyMoment[] | null {
  const [firstMoment, ...rest] = order.journey_moments ?? []
  const complete = order.journey_moments_count != null && (order.journey_moments?.length ?? 0) >= order.journey_moments_count
  if (!firstMoment || !complete) {
    return null
  }

  const momentFills: Partial<Record<UtmTargetField, string>> = {}
  for (const [key, value] of Object.entries(fills) as [UtmOrderKey, string][]) {
    if (key.startsWith("first_")) {
      momentFills[key.slice("first_".length) as UtmTargetField] = value
    }
  }

  return Object.keys(momentFills).length > 0 ? [{ ...firstMoment, ...momentFills }, ...rest] : null
}

// GraphQL landingPage strips query strings; REST landing_site keeps them and maps to the FIRST visit.
function collectParamSources(order: AttributionOrder, side: VisitSide): URLSearchParams[] {
  const sources: URLSearchParams[] = []

  const landingPage = side === "first" ? order.first_landing_page : order.last_landing_page
  const fromLandingPage = parseLandingPageParams(landingPage)
  if (fromLandingPage) sources.push(fromLandingPage)

  if (side === "first") {
    const fromLandingSite = parseLandingPageParams(order.landing_site_rest)
    if (fromLandingSite) sources.push(fromLandingSite)
  }

  return sources
}

function fillIfEmpty(
  order: AttributionOrder,
  fills: Partial<Record<UtmOrderKey, string>>,
  side: VisitSide,
  field: UtmTargetField,
  value: string,
): void {
  const key: UtmOrderKey = `${side}_${field}`
  const current = fills[key] ?? order[key]
  if (typeof current === "string" && current.trim()) return
  fills[key] = value
}

// Landing pages may be relative paths or malformed (e.g. unexpanded ad-template tokens) — never throw.
function parseLandingPageParams(landingPage: string | null | undefined): URLSearchParams | null {
  if (typeof landingPage !== "string" || !landingPage.trim()) {
    return null
  }

  try {
    return new URL(landingPage, "https://placeholder.invalid").searchParams
  } catch {
    return null
  }
}
